import type { ItemView, UpdateItemRequest } from '@fall26/shared'

/** The editable text of an item, as the sheet holds it while you type. */
export interface Draft {
  title: string
  detail: string
  dueDate: string
  dueTime: string
}

export function draftOf(item: ItemView): Draft {
  return {
    title: item.title,
    detail: item.detail,
    dueDate: item.due.date ?? '',
    dueTime: item.due.time ?? '',
  }
}

/**
 * What actually changed — and nothing else.
 *
 * This is the load-bearing function in the editor, and not for tidiness.
 * The server records an override row for EVERY field a PATCH names, and an
 * override is a permanent claim: it stops a later Canvas sync from ever
 * updating that field again without asking me first.
 *
 * So sending the whole form on save would silently claim ownership of every
 * field I did not touch. Open the sheet on a Canvas-linked item, fix a typo in
 * the title, hit save, and its due date would quietly stop tracking Canvas —
 * with no message, and no way to notice until a deadline moved and the app
 * didn't say so.
 *
 * An empty result means there is nothing to send, which the caller treats as
 * "no save", not as an empty PATCH the server would reject.
 */
export function changedFields(item: ItemView, draft: Draft): UpdateItemRequest {
  const patch: UpdateItemRequest = {}

  if (draft.title.trim() !== item.title) patch.title = draft.title.trim()
  if (draft.detail !== item.detail) patch.detail = draft.detail

  // '' from an emptied date input means "no date", which is a real value —
  // clearing a date is a legitimate edit and must not read as "unchanged".
  const date = draft.dueDate === '' ? null : draft.dueDate
  if (date !== (item.due.date ?? null)) patch.dueDate = date as UpdateItemRequest['dueDate']

  const time = draft.dueTime === '' ? null : draft.dueTime
  if (time !== (item.due.time ?? null)) patch.dueTime = time as UpdateItemRequest['dueTime']

  return patch
}
